import React, { useCallback, useState, useEffect } from 'react';
import styled from "@emotion/styled";

import { COLORS, COLORS_BASE } from '../../utils/consts';

import Toggle from '../Shared/Toggle';
import { MapType, SelectedLayersType } from '../../types/dataTypes';
import TooltipWrapper from '../Shared/TooltipWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type ViewLayerRasterProps = {
  map: MapType;
  rasterLayers: SelectedLayersType[];
  setSelectedLayers: Function;
};

//TODO add the opacity slider to the legend
function ViewLayerRaster({ map, rasterLayers, setSelectedLayers }: ViewLayerRasterProps) {
  const mapCurrent = map.current;
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(
    rasterLayers.find(layer => layer.isSelected)?.layerId || null
  );
  const [opacity, setOpacity] = useState<number>(1);

  const handleToggle = useCallback((layerId: string) => {
    setSelectedLayerId(prevSelectedLayerId => {
      const newSelectedLayerId = prevSelectedLayerId === layerId ? null : layerId;
      return newSelectedLayerId;
    });
  }, []);

  useEffect(() => {
    setSelectedLayers((prev: SelectedLayersType[]) =>
      prev.map((layer) =>
        layer.type === 'raster'
          ? { ...layer, isSelected: layer.layerId === selectedLayerId }
          : layer
      )
    );
  }, [selectedLayerId, setSelectedLayers]);

  const handleOpacityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newOpacity = parseFloat(event.target.value);
    setOpacity(newOpacity);
  };

  useEffect(() => {
    if (mapCurrent) {
      rasterLayers.forEach(layer => {
        const visibility = layer.layerId === selectedLayerId ? 'visible' : 'none';
        mapCurrent.setLayoutProperty(layer.layerId, 'visibility', visibility);
        mapCurrent.setPaintProperty(layer.layerId, 'raster-opacity', opacity);
      });
    }
  }, [mapCurrent, selectedLayerId, rasterLayers, opacity]);

  return (
    <>
    <SubContainerStyle>
      {rasterLayers.map((layer) => (
        <ContainerFormStyle key={layer.layerId} name='Show Layer Form'>
          <ContainerLabelStyle htmlFor={layer.layerId} toggle={layer.isSelected}>
            <Toggle
              layerId={layer.layerId}
              toggle={layer.isSelected}
              toggleLayer={() => handleToggle(layer.layerId)}
              radio={false} // Change to checkbox behavior
            />
            {layer.layerName}
          <TooltipWrapper content={layer.toolTipText} positionFromDivBottom="35px" margin="0 0 0 5px">
            <FontAwesomeIcon icon="info-circle" /> 
          </TooltipWrapper>
          </ContainerLabelStyle>
        </ContainerFormStyle>
      ))}
    </SubContainerStyle>
    {selectedLayerId && (
      <OpacitySliderContainer>
        <OpacitySliderLabel>Opacity:</OpacitySliderLabel>
        <OpacitySlider
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={opacity}
          onChange={handleOpacityChange}
        />
        <OpacityValue>{opacity}</OpacityValue>
      </OpacitySliderContainer>
    )}
    </>
  );
}

export default ViewLayerRaster;

const SubContainerStyle = styled.div`
  margin: 8px 0;
`;

const ContainerFormStyle = styled.form<{}>(() => `
  margin: 8px 0;
`);

const ContainerLabelStyle = styled.label<{ toggle?: boolean }>(({ toggle = true }) => `
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  position: relative;
  ${!toggle && `color: ${COLORS.FONT.INACTIVE};`}
`);

const OpacitySliderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const OpacitySliderLabel = styled.span`
  margin-right: 10px;
  font-size: 14px;
  color: ${COLORS_BASE.BRAND.MAIN};
  font-weight: bold;
`;

const OpacityValue = styled.span`
  margin-left: 10px;
  font-size: 14px;
  color: ${COLORS_BASE.BRAND.MAIN};
  font-weight: bold;
`;

const OpacitySlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 8px;
  background: linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
  outline: none;
  opacity: 0.7;
  transition: opacity .2s;

  &:hover {
    opacity: 1;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: ${COLORS_BASE.BRAND.MAIN};
    cursor: pointer;
    border-radius: 50%;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: ${COLORS_BASE.BRAND.MAIN};
    cursor: pointer;
    border-radius: 50%;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  }
`;